<template>
    <div
        class="flex flex-col justify-between items-center border border-black border-opacity-10 rounded-md bg-white box-shadow cursor-pointer p-4 relative transition hover:-translate-y-2 h-full"
        :class="!isEditable && 'pointer-events-none'"
    >
        <div class="lg:w-full" :class="isLarge ? 'w-[90%]' : 'w-full'">
            <div
                class="aspect-w-1 aspect-h-1 relative"
                :class="product.article_number === 'rosebox-1' ? 'mb-2' : 'mb-1'"
            >
                <!-- editor captures already contain their frame and shadow -->
                <img
                    v-if="product?.extras?.frame?.id && !editor"
                    :src="require(`~/assets/images/dev/frames/frame_${product.extras.frame.id}.webp`)"
                    class="absolute w-full h-full top-0 left-0 object-contain preview-frame product-image opacity-100 z-1 pointer-events-none"
                    :class="product?.orientation === 'horizontal' && 'rotate-90 ml-1'"
                    :alt="$t('checkout.product.frame_alt')"
                />
                <nuxt-link :to="setProductUrl(product)">
                    <img
                        v-if="product.article_number === 'rosebox-1'"
                        :src="
                            require(`~/assets/images/whispers-of-wood/rose-colors/${
                                product.extras.color.options.find((color) => color.id === product.extras.color.value)
                                    .preview
                            }`)
                        "
                        class="w-full h-full object-contain relative z-10"
                    />
                    <app-image
                        v-else
                        class="w-full h-full object-contain relative z-10"
                        :src="product?.image || ''"
                        :alt="product?.name || ''"
                        preset="card"
                    />
                </nuxt-link>
                <img
                    v-if="!editor"
                    class="absolute w-full h-full top-0 left-0 object-contain z-1 preview-shadow opacity-100 pointer-events-none"
                    :src="
                        require(`assets/images/dev/star_map_editor/${
                            product?.orientation === 'horizontal' ? 'shadow--horizontal.webp' : 'shadow.webp'
                        }`)
                    "
                    :alt="$t('checkout.product.shadow_alt')"
                />
            </div>
            <h4
                class="font-Inter-Bold lg:text-base mb-4 leading-5 lg:leading-6 w-full block pb-2 pr-4 lg:pr-0 border-b border-black border-opacity-5"
                :class="isLarge ? 'text-base' : 'text-sm'"
            >
                {{ product?.name }}
            </h4>
            <h5 class="flex mb-1 text-base justify-between lg:text-sm" :class="isLarge ? 'text-sm' : 'text-xs'">
                <span class="w-auto lg:w-1/2 mr-1 lg:mr-0 font-Inter-Bold text-left">{{ $t('checkout.product.total_price') }}</span>
                <span class="w-auto lg:w-1/2 text-right">{{
                    $formatCurrency(product?.price + calculateTotalPrice(product.extras))
                }}</span>
            </h5>
            <h5
                v-if="product?.extras?.size"
                class="flex mb-1 lg:text-sm justify-between mt-2"
                :class="isLarge ? 'text-sm' : 'text-xs'"
            >
                <span class="w-auto lg:w-1/3 mr-1 lg:mr-0 font-Inter-Bold text-left">{{ $t('checkout.product.size') }}</span>
                <div class="w-auto lg:w-2/3 text-right">
                    {{ product?.extras?.size.name }}
                    <span v-if="product.extras.size.price > 0">({{ $formatCurrency(product.extras.size.price) }})</span>
                </div>
            </h5>
            <h5
                v-if="product?.extras?.frame?.id"
                class="flex mb-1 text-base justify-between mt-2 lg:text-sm"
                :class="isLarge ? 'text-sm' : 'text-xs'"
            >
                <span class="w-auto lg:w-1/3 mr-1 lg:mr-0 font-Inter-Bold text-left">{{ $t('checkout.product.frame') }}</span>
                <div class="w-auto lg:w-2/3 text-right">
                    {{ product?.extras?.frame.name }}
                    <span v-if="product.extras.frame.price > 0"
                        >({{ $formatCurrency(product.extras.frame.price) }})</span
                    >
                </div>
            </h5>
            <h5 class="flex mb-1 text-base justify-between mt-2 lg:text-sm" :class="isLarge ? 'text-sm' : 'text-xs'">
                <span class="w-auto lg:w-1/2 mr-1 lg:mr-0 font-Inter-Bold text-left">{{ $t('checkout.product.digital_too') }}</span>
                <span class="w-auto lg:w-1/2 text-right">{{
                    product?.extras?.hasDigital?.active ? $t('checkout.yes') : $t('checkout.no')
                }}</span>
            </h5>
        </div>
        <div v-if="isEditable" class="h-auto flex flex-col justify-between w-full mt-2">
            <div class="flex flex-col">
                <div class="order-3 w-full text-center mt-1">
                    <div class="hidden lg:flex mb-1 text-base items-center">
                        <span class="w-auto lg:w-1/2 mr-1 lg:mr-0 font-Inter-Bold text-xs lg:text-sm text-left"
                            >{{ $t('checkout.product.quantity_label') }}</span
                        >
                        <div class="w-3/4 text-center flex justify-end quantity-input-wrapper">
                            <Input
                                v-model="localProduct.quantity"
                                is-number-incrementer
                                :placeholder="$t('checkout.product.quantity')"
                                :required="false"
                                class="w-[6rem] h-[3.5em] rounded-[2px] number-input text-center quantity flex items-center"
                                @input="handleQuantityChange()"
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <button
            v-if="isEditable"
            class="absolute right-0 top-0 transition-all z-30 p-2 rounded-full bg-white scale-90 hover:scale-100"
            @click.prevent="removeProductFromCart(product.cartItemId)"
        >
            <i
                class="text-2xl"
                :class="
                    isDeleting
                        ? 'ri-loader-4-line animate-spin block transition leading-none pointer-events-none'
                        : 'ri-close-line'
                "
            ></i>
        </button>
    </div>
</template>

<script>
export default {
    name: 'CartProductCard',
    props: {
        product: {
            type: Object,
            required: true,
        },
        quantity: {
            type: Number,
            required: true,
        },
        isLarge: {
            type: Boolean,
            required: false,
            default: false,
        },
        removeCb: {
            type: Function,
            required: false,
            default: () => {},
        },
        updateCb: {
            type: Function,
            required: false,
            default: () => {},
        },
        isEditable: {
            type: Boolean,
            required: false,
            default: true,
        },
    },
    data() {
        return {
            isDeleting: false,
            localProduct: { ...this.product }, // Create a local copy of product
        };
    },
    computed: {
        computedValue: {
            get() {
                return this.product.quantity;
            },
            set(newValue) {
                this.$emit('input', newValue);
            },
        },
        // wood maps currently share the poster product ids, so extras decide which editor made the product
        editor() {
            const extras = this.product?.extras || {};
            return (
                extras.editor ||
                (extras.roadsWood && 'wood-map') ||
                ((extras.skyWood || extras.textWood) && 'wood-star-map') ||
                (extras.landWood && extras.layerCount && 'topo-map') ||
                (this.product?.id === 125 && 'map') ||
                (this.product?.id === 126 && 'star-map')
            );
        },
    },
    watch: {
        // Keep localProduct in sync with prop changes
        product: {
            handler(newVal) {
                this.localProduct = { ...newVal };
            },
            deep: true,
        },
        quantity(newVal) {
            this.localProduct.quantity = newVal; // Update local quantity
        },
    },
    methods: {
        calculateTotalPrice(data) {
            if (!data) return 0;

            const getPrice = (item) => {
                if (typeof item === 'object' && item !== null) {
                    if (Object.prototype.hasOwnProperty.call(item, 'price')) {
                        return parseFloat(item.price) || 0;
                    }

                    return Object.values(item).reduce((total, subItem) => total + getPrice(subItem), 0);
                }
                return 0;
            };

            return getPrice(data);
        },
        setProductUrl(product) {
            const extras = product?.extras || {};
            const editor = this.editor;
            const editorRoutes = {
                'wood-map': 'editors-wood-map',
                'wood-star-map': 'editors-wood-star-map',
                'topo-map': 'editors-topo-map',
                'city-map': 'editors-city-map',
                map: 'editors-map',
                'star-map': 'editors-star-map',
            };

            if (editorRoutes[editor]) {
                return this.localePath({
                    name: editorRoutes[editor],
                    hash: extras.hash ? `#${product.cartItemId}#${extras.hash}` : '',
                });
            }

            return this.localePath({
                name: 'products-category-product',
                params: {
                    category: product?.category_slug,
                    product: product?.slug,
                },
            });
        },
        async handleQuantityChange() {
            if (this.localProduct.quantity !== this.product.quantity) {
                await this.updateCart();
            }
        },
        async updateCart() {
            try {
                const res = await this.$axios.$post('/cart-item-add', {
                    product_id: this.localProduct.id,
                    quantity: this.localProduct.quantity,
                    session_id: this.$cookies.get('hsession'),
                    image: this.localProduct.image,
                    extras: this.localProduct.extras,
                    update: 1,
                });

                if (res.success) {
                    this.updateCb(); // Refresh parent component if needed
                }
            } catch (err) {
                console.error('Failed to update cart:', err);
            }
        },
        async removeProductFromCart(id) {
            this.isDeleting = true;
            try {
                await this.$axios
                    .$post('/cart-item-delete', {
                        id,
                        session_id: this.$cookies.get('hsession'),
                    })
                    .then((res) => {
                        this.isDeleting = false;
                        if (res.success === true) {
                            this.removeCb();
                        }
                    });
            } catch (err) {
                console.log(err);
            }
        },
    },
};
</script>

<style scoped>
.quantity-input-wrapper {
    .p-inputtext {
        min-height: 1.5rem !important;
    }
}

.pi.pi-plus::before,
.pi.pi-minus::before {
    font-size: 0.75rem !important;
}
</style>
